//React
import React from "react";
//React-Table
import { useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";
//Material UI
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
//Components
import ButtonEditUser from "./ButtonEditUser";
import InactiveRowActions from "./InactiveRowActions";
import { GhostButton } from "../../../Global/other/Buttons";
import SegToggle from "../../../Global/other/SegToggle";

/**
 * Users table, built to NeuraDesigns `userport/index.html`.
 *
 * Columns are Name · Email · Role · Region · actions. Group was dropped as
 * redundant with Role.
 *
 * Role and Region both come from the user's `user_groups`, which the list
 * endpoint already returns, joined against the `user_groups_available`
 * taxonomy — `get_user_groups_available_subdivided()` appends a synthesised
 * "IMS Regions" group, which is what separates a region membership from a
 * role. Both queries are already cached by RTK Query, so this costs no extra
 * request beyond the one ButtonAddUser/ButtonEditUser already make.
 */

const REGION_GROUP_NAME = "IMS Regions";
const REGION_PREFIX = "ims_region_";
const PAGE_SIZES = [10, 20, 50, 100];

// Module scope so these keep a stable identity across renders. react-table
// treats a changed `data`/`initialState` reference as a real change.
const INITIAL_STATE = { pageSize: 20 };
const NO_GROUPS = [];

/** unique_name -> { label, isRegion } */
function buildGroupIndex(availableGroups) {
  const index = new Map();
  (availableGroups || []).forEach((group) => {
    const isRegion = group.group_name === REGION_GROUP_NAME;
    (group.user_groups || []).forEach((entry) => {
      index.set(entry.unique_name, { label: entry.human_readable_name, isRegion });
    });
  });
  return index;
}

function splitMemberships(user, index) {
  const roles = [];
  const regions = [];
  (user.user_groups || []).forEach((membership) => {
    const known = index.get(membership.unique_name);
    // Fall back to the raw name so a group the taxonomy has not caught up
    // with still shows, rather than silently vanishing from the row.
    const label = known ? known.label : membership.unique_name;
    const isRegion = known ? known.isRegion : membership.unique_name.startsWith(REGION_PREFIX);
    (isRegion ? regions : roles).push(label);
  });
  return { roles, regions };
}

/** Page numbers to render, keeping first, last and current +/- 1. */
function pageWindow(current, total) {
  const keep = [1, total, current - 1, current, current + 1]
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const out = [];
  let previous = 0;
  keep.forEach((n) => {
    if (n === previous) return;
    if (previous && n - previous > 1) out.push("ellipsis");
    out.push(n);
    previous = n;
  });
  return out;
}

function Chips({ values }) {
  if (!values.length) return <Box component="span" sx={{ color: "var(--text-faint)" }}>—</Box>;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
      {values.map((value) => (
        <Box
          key={value}
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "0.72rem",
            fontWeight: 600,
            padding: "3px 10px",
            borderRadius: "999px",
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
            background: "rgba(255, 255, 255, 0.06)",
            color: "rgba(255, 255, 255, 0.75)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
          }}
        >
          {value}
        </Box>
      ))}
    </Box>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

const pageButtonSx = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "6px",
  background: "transparent",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  color: "rgba(255, 255, 255, 0.6)",
  cursor: "pointer",
  fontSize: "0.78rem",
  fontFamily: "inherit",
  "&:hover:not(:disabled)": { color: "var(--accent)", borderColor: "rgba(253, 185, 64, 0.3)" },
  "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
};

export default function TableUsers({ user_data, available_groups = NO_GROUPS }) {
  const [view, setView] = React.useState("active");
  const groupIndex = React.useMemo(() => buildGroupIndex(available_groups), [available_groups]);

  // Memberships are resolved once per user rather than inside each cell, so
  // sorting and the global filter can read them as plain strings.
  const allRows = React.useMemo(
    () =>
      (user_data || []).map((user) => {
        const { roles, regions } = splitMemberships(user, groupIndex);
        return {
          ...user,
          displayName: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
          roles,
          regions,
          rolesText: roles.slice().sort().join(", "),
          regionsText: regions.slice().sort().join(", "),
        };
      }),
    [user_data, groupIndex]
  );

  // Counts are of the whole set per status, not of the current search — the
  // toggle reports how many exist, not how many match.
  const counts = React.useMemo(
    () => ({
      active: allRows.filter((u) => u.is_active !== false).length,
      inactive: allRows.filter((u) => u.is_active === false).length,
    }),
    [allRows]
  );

  // `is_active !== false` rather than `=== true`: a backend that has not yet
  // been updated omits the field entirely, and those users are active.
  const data = React.useMemo(
    () =>
      view === "active"
        ? allRows.filter((u) => u.is_active !== false)
        : allRows.filter((u) => u.is_active === false),
    [allRows, view]
  );

  const columns = React.useMemo(
    () => [
      { Header: "Name", accessor: "displayName" },
      { Header: "Email", accessor: "email" },
      {
        Header: "Role",
        accessor: "rolesText",
        Cell: ({ row }) => <Chips values={row.original.roles} />,
      },
      {
        Header: "Region",
        accessor: "regionsText",
        Cell: ({ row }) => <Chips values={row.original.regions} />,
      },
      {
        Header: "",
        id: "actions",
        disableSortBy: true,
        // Inactive rows offer no edit — reactivate first, then edit, which is
        // the pattern the design reuses from ims/installers.html.
        Cell: ({ row }) =>
          view === "active" ? (
            <ButtonEditUser user={row.original} />
          ) : (
            <InactiveRowActions user={row.original} />
          ),
      },
    ],
    [view]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    pageCount,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data,
      initialState: INITIAL_STATE,
      // react-table resets page, sort and filter whenever `data` changes
      // identity. That is both a render loop waiting to happen and the wrong
      // behaviour: the design calls out explicitly that editing a user must
      // re-render in place without throwing away the current page, sort or
      // search.
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetGlobalFilter: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const total = rows.length;
  const firstRow = total === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min((pageIndex + 1) * pageSize, total);

  return (
    <Box
      sx={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-xl)",
      }}
    >
      {/* Toolbar */}
      <Box sx={{ position: "relative", borderBottom: "1px solid var(--border)" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px",
            padding: "14px 20px 12px",
          }}
        >
          <SegToggle
            ariaLabel="User status"
            value={view}
            onChange={(next) => {
              setView(next);
              gotoPage(0);
            }}
            options={[
              { value: "active", label: "Active", count: counts.active },
              { value: "inactive", label: "Inactive", count: counts.inactive },
            ]}
          />

          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              padding: "6px 12px",
              borderRadius: "999px",
              marginLeft: "auto",
              minWidth: "220px",
              transition: "border-color 0.16s var(--ease-out), box-shadow 0.16s var(--ease-out)",
              // The mockup gives the search pill no focus style at all; this
              // matches the focus treatment used by every other field.
              "&:focus-within": {
                borderColor: "rgba(253, 185, 64, 0.45)",
                boxShadow: "0 0 0 3px rgba(253, 185, 64, 0.10)",
              },
              "@media (max-width: 720px)": { marginLeft: 0, flex: "1 1 100%", order: 3 },
              "& svg": { color: "rgba(255, 255, 255, 0.4)", flexShrink: 0 },
            }}
          >
            <SearchIcon />
            <Box
              component="input"
              type="text"
              autoComplete="off"
              placeholder="Search name, email, role, region"
              value={globalFilter || ""}
              onChange={(event) => {
                setGlobalFilter(event.target.value || undefined);
                gotoPage(0);
              }}
              sx={{
                background: "transparent",
                border: "none",
                color: "var(--text)",
                fontFamily: "inherit",
                fontSize: "0.82rem",
                outline: "none",
                width: "100%",
                "&::placeholder": { color: "var(--text-faint)" },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          {...getTableProps()}
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.84rem",
            "& thead th": {
              textAlign: "left",
              fontSize: "0.68rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "rgba(255, 255, 255, 0.45)",
              fontWeight: 600,
              padding: "12px 16px",
              background: "rgba(255, 255, 255, 0.02)",
              borderBottom: "1px solid var(--border)",
              whiteSpace: "nowrap",
            },
            "& tbody tr": {
              borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
              transition: "background 0.15s",
            },
            "& tbody tr:hover": { background: "rgba(253, 185, 64, 0.04)" },
            "& tbody tr:last-child": { borderBottom: "none" },
            "& tbody td": {
              padding: "12px 16px",
              color: "rgba(255, 255, 255, 0.85)",
              verticalAlign: "middle",
            },
          }}
        >
          <thead>
            {headerGroups.map((headerGroup) => {
              // react-table v7 returns `key` inside its prop getters. React 18
              // rejects a key arriving via spread, so it is pulled out and
              // passed explicitly here and on every row and cell below.
              const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
              <tr key={headerGroupKey} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const sortable = !column.disableSortBy;
                  const { key: columnKey, ...columnProps } = column.getHeaderProps(
                    sortable ? column.getSortByToggleProps() : undefined
                  );
                  return (
                    <Box
                      component="th"
                      key={columnKey}
                      {...columnProps}
                      aria-sort={
                        column.isSorted ? (column.isSortedDesc ? "descending" : "ascending") : undefined
                      }
                      sx={{
                        width: column.id === "actions" ? "76px" : undefined,
                        cursor: sortable ? "pointer" : "default",
                        userSelect: "none",
                        "&:hover": sortable ? { color: "rgba(255, 255, 255, 0.7)" } : undefined,
                        // The design's sort affordance is a CSS triangle that
                        // is always present at 25% and lifts to 90% when
                        // active, replacing the old 🔽/🔼 emoji.
                        "&::after": sortable
                          ? {
                              content: '""',
                              display: "inline-block",
                              width: 0,
                              height: 0,
                              marginLeft: "5px",
                              verticalAlign: "middle",
                              borderLeft: "4px solid transparent",
                              borderRight: "4px solid transparent",
                              borderBottom: column.isSorted && column.isSortedDesc ? "none" : "4px solid currentColor",
                              borderTop: column.isSorted && column.isSortedDesc ? "4px solid currentColor" : "none",
                              opacity: column.isSorted ? 0.9 : 0.25,
                            }
                          : undefined,
                      }}
                    >
                      {column.render("Header")}
                    </Box>
                  );
                })}
              </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <Box
                  component="td"
                  colSpan={columns.length}
                  sx={{
                    textAlign: "center",
                    color: "rgba(255, 255, 255, 0.55)",
                    fontSize: "0.9rem",
                    padding: "48px 20px !important",
                  }}
                >
                  {globalFilter
                    ? "No users match your search."
                    : view === "inactive"
                    ? "No inactive users."
                    : "No users found."}
                  {globalFilter ? (
                    <GhostButton
                      type="button"
                      onClick={() => setGlobalFilter(undefined)}
                      sx={{ marginLeft: "8px", padding: "5px 12px" }}
                    >
                      Clear search
                    </GhostButton>
                  ) : null}
                </Box>
              </tr>
            ) : (
              page.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps();
                      return (
                        <Box
                          component="td"
                          key={cellKey}
                          {...cellProps}
                          sx={cell.column.id === "actions" ? { textAlign: "right", width: "76px", whiteSpace: "nowrap" } : undefined}
                        >
                          {cell.render("Cell")}
                        </Box>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          padding: "14px 20px",
          borderTop: "1px solid rgba(255, 255, 255, 0.05)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          fontSize: "0.78rem",
          color: "var(--text-muted)",
          flexWrap: "wrap",
        }}
      >
        <Box component="span">
          {total === 0
            ? `Showing 0 of ${data.length} users`
            : `Showing ${firstRow}–${lastRow} of ${total} users`}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box component="span">Rows per page</Box>
            <Select
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              inputProps={{ "aria-label": "Rows per page" }}
              MenuProps={{ anchorOrigin: { vertical: "top", horizontal: "left" }, transformOrigin: { vertical: "bottom", horizontal: "left" } }}
              sx={{
                "& .MuiSelect-select": { padding: "5px 8px !important", fontSize: "0.78rem" },
                "& fieldset": { borderColor: "rgba(255, 255, 255, 0.08)" },
                "& .MuiSelect-icon": { color: "var(--text-muted)" },
              }}
            >
              {PAGE_SIZES.map((size) => (
                <MenuItem key={size} value={size} sx={{ fontSize: "0.82rem" }}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* The design shows the pager only when there is more than one page. */}
          {total > pageSize ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                aria-label="Previous page"
                sx={pageButtonSx}
              >
                ‹
              </Box>
              {pageWindow(pageIndex + 1, pageCount).map((entry, position) =>
                entry === "ellipsis" ? (
                  <Box
                    key={`gap-${position}`}
                    component="span"
                    sx={{ padding: "0 4px", color: "var(--text-faint)", fontSize: "0.78rem" }}
                  >
                    …
                  </Box>
                ) : (
                  <Box
                    key={entry}
                    component="button"
                    type="button"
                    onClick={() => gotoPage(entry - 1)}
                    aria-current={entry === pageIndex + 1 ? "page" : undefined}
                    sx={{
                      ...pageButtonSx,
                      ...(entry === pageIndex + 1
                        ? {
                            background: "rgba(253, 185, 64, 0.14)",
                            borderColor: "rgba(253, 185, 64, 0.4)",
                            color: "var(--accent)",
                          }
                        : {}),
                    }}
                  >
                    {entry}
                  </Box>
                )
              )}
              <Box
                component="button"
                type="button"
                onClick={() => nextPage()}
                disabled={!canNextPage}
                aria-label="Next page"
                sx={pageButtonSx}
              >
                ›
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
